import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../../../utils/firebase';

/** Blooket 플레이 링크 — 연수 전에 실제 게임 ID로 교체하세요 */
const BLOOKET_PLAY_URL = 'https://play.blooket.com/play';

const CANVA_INVITE_URL =
  'https://www.canva.com/brand/join?token=wysBVGQOyWBAmFRPe_5SBA&brandingVariant=edu&referrer=team-invite';

const SEAMSPACE_LOGIN_URL = 'https://diary.seamspace.me/login';

const SEN_SCHOOL_URL = 'https://senedu.kr';

/** 연수 발표 자료 PPT (OneDrive 공유 — 새 탭) */
const TRAINING_PPT_URL =
  'https://1drv.ms/p/c/f326a53b9af299ff/IQSroV32P0HxR4mpXxV-EsgSATqjoV9s2vyrNv8tTQIQwBE';

/** PPT 웹 뷰어 iframe용(OneDrive 임베드) */
const TRAINING_PPT_EMBED_SRC = `${TRAINING_PPT_URL}?em=2&wdAr=1.7777777777777777`;

const WORKSHEET_STORAGE_PATH =
  'notes_public_training/NamdaemoonMiddleSchoolDebethTraining2026/simple_math_worksheet_galaxytab.pdf';
const WORKSHEET_DOWNLOAD_LABEL = '학습지 예시 다운로드 (갤럭시탭용 PDF)';

export const trainingMetadataNamdaemoonDebeth2026 = {
  id: 'namdaemoon-middle-debeth-2026',
  title: '남대문중학교 디벗 연수',
  date: '2026-06-11',
  path: '/teacher-training/digital/NamdaemoonMiddleSchoolDebethTraining2026',
};

const infoBoxStyle: React.CSSProperties = {
  backgroundColor: 'var(--Color-background-normal)',
  border: '1px solid var(--Color-line-normal)',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
};

const NamdaemoonMiddleSchoolDebethTraining2026: React.FC = () => {
  const navigate = useNavigate();
  const [worksheetDownloadUrl, setWorksheetDownloadUrl] = useState<string | null>(null);
  const [worksheetDownloadError, setWorksheetDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const loadWorksheetDownloadUrl = async () => {
      try {
        const url = await getDownloadURL(ref(storage, WORKSHEET_STORAGE_PATH));
        if (mounted) {
          setWorksheetDownloadUrl(url);
          setWorksheetDownloadError(null);
        }
      } catch (error) {
        console.warn('학습지 예시 파일 링크 조회 실패:', error);
        if (mounted) {
          setWorksheetDownloadUrl(null);
          setWorksheetDownloadError('학습지 예시 파일을 찾지 못했습니다. Storage 경로를 확인해 주세요.');
        }
      }
    };
    loadWorksheetDownloadUrl();
    return () => {
      mounted = false;
    };
  }, []);

  const linkButton = (href: string, label: string, external = true) => (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={{
        display: 'inline-block',
        marginTop: '8px',
        padding: '10px 18px',
        backgroundColor: 'var(--Color-fill-brand-color)',
        color: 'var(--Color-text-inverse)',
        textDecoration: 'none',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '14px',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      {label} →
    </a>
  );

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    marginTop: '12px',
  };

  const thStyle: React.CSSProperties = {
    border: '1px solid var(--Color-line-normal)',
    padding: '10px 12px',
    backgroundColor: 'var(--Color-background-main)',
    textAlign: 'left',
    fontWeight: 600,
    color: 'var(--Color-text-strong)',
  };

  const tdStyle: React.CSSProperties = {
    border: '1px solid var(--Color-line-normal)',
    padding: '10px 12px',
    color: 'var(--Color-text-normal)',
    lineHeight: 1.6,
    verticalAlign: 'top',
  };

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        padding: '24px',
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <button
          type="button"
          onClick={() => navigate('/teacher-training/training-pages')}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--Color-fill-normal)',
            color: 'var(--Color-text-inverse)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          ← 돌아가기
        </button>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: 0,
            color: 'var(--Color-text-strong)',
            lineHeight: 1.4,
          }}
        >
          {trainingMetadataNamdaemoonDebeth2026.title}
        </h1>
      </div>

      <div
        style={{
          marginBottom: '24px',
          border: '1px solid var(--Color-line-normal)',
          padding: '16px',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-normal)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <h2 style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.125rem', color: 'var(--Color-text-strong)' }}>
          목차
        </h2>
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
          {[
            ['#연수-개요', '연수 개요'],
            ['#순서-1', '1. Blooket 퀴즈 오프닝'],
            ['#순서-2', '2. 최근 AI·디지털 동향'],
            ['#순서-3', '3. 학습지원 소프트웨어 심의'],
            ['#순서-4', '4. SEN 스쿨 활용'],
            ['#순서-5', '5. Canva · Seam 스페이스'],
            ['#순서-6', '6. 전자칠판 사용 주의사항'],
            ['#연수-자료', '연수 발표 자료 (PPT)'],
            ['#학습지-예시', '학습지 예시 다운로드'],
          ].map(([hash, label]) => (
            <li key={hash} style={{ marginBottom: '8px' }}>
              <a
                href={hash}
                style={{
                  textDecoration: 'none',
                  color: 'var(--Color-fill-brand-color)',
                  fontWeight: 500,
                  fontSize: '15px',
                }}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <h2
        id="연수-개요"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        연수 개요
      </h2>
      <div style={{ ...infoBoxStyle, marginBottom: '20px' }}>
        <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          주제: AI · EdTech · 디벗 · 스마트 수업 — 디벗을 활용한 수업설계
        </p>
        <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          대상: 남대문중학교 교직원
        </p>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          강사: 한지호
        </p>
      </div>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '16px', lineHeight: 1.7, fontSize: '15px' }}>
        오늘의 핵심 질문은 &ldquo;어떤 도구를 쓸 것인가?&rdquo;가 아니라, &ldquo;AI 환경 속에서 어떤 학습 경험을 설계할
        것인가?&rdquo;입니다. 연수 순서는 다음과 같습니다.
      </p>
      <ol
        style={{
          color: 'var(--Color-text-normal)',
          marginBottom: '28px',
          lineHeight: 1.8,
          fontSize: '15px',
          paddingLeft: '22px',
        }}
      >
        <li>Blooket 퀴즈 오프닝</li>
        <li>최근 AI·디지털 동향</li>
        <li>학습지원 소프트웨어 심의 내용</li>
        <li>SEN 스쿨 활용 (회원가입 및 활용 방안)</li>
        <li>Canva, Seam 스페이스 사용</li>
        <li>전자칠판 사용 주의사항</li>
      </ol>

      <h2
        id="순서-1"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        1. Blooket 퀴즈 오프닝
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        연수 시작 전, Blooket 퀴즈로 오늘의 주제를 가볍게 점검해 봅니다. 아래 버튼을 눌러 게임에 참여하세요.
      </p>
      <div
        style={{
          ...infoBoxStyle,
          marginBottom: '32px',
          border: '1px dashed var(--Color-line-normal)',
        }}
      >
        {BLOOKET_PLAY_URL ? (
          <div>{linkButton(BLOOKET_PLAY_URL, 'Blooket 퀴즈 참여하기')}</div>
        ) : (
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
            Blooket 플레이 링크가 아직 등록되지 않았습니다. 연수 당일 강사가 공유하는 게임 코드 또는 링크로
            참여해 주세요.
          </p>
        )}
      </div>

      <h2
        id="순서-2"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        2. 최근 AI·디지털 동향
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '16px', lineHeight: 1.7, fontSize: '15px' }}>
        AI는 더 이상 특정 앱이 아니라, 교실 안에서 학습 경험을 설계하는 환경으로 확장되고 있습니다.
      </p>

      <div style={infoBoxStyle}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>
          TREND 1 — AI가 현실 세계로 나온다
        </h3>
        <p style={{ margin: 0, color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          Hyundai × Boston Dynamics 로봇 캠페인처럼, 자동차 회사가 제품을 넘어 &ldquo;경험&rdquo;을 설계하는 사례가
          늘고 있습니다. 손흥민 × Atlas 영상은 로봇을 미래 이동 경험의 상징으로 제시합니다.
        </p>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>
          TREND 2 — AI가 기기 생태계 전체로 퍼진다
        </h3>
        <p style={{ margin: 0, color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          Android Show의 Gemini Intelligence처럼 AI는 특정 앱이 아니라 폰·웨어러블·자동차를 연결합니다. 수업도
          앱 하나를 배우는 방식에서, 학습 흐름을 연결하는 방식으로 이동해야 합니다.
        </p>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>
          TREND 3 — AI가 도구를 만든다
        </h3>
        <p style={{ margin: 0, color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          WWDC의 Shortcuts·위젯처럼, 학생은 앱 기능을 익히는 소비자를 넘어 자신의 문제를 해결하는 도구를 설계하는
          사람으로 성장해야 합니다.
        </p>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>
          TREND 4 — 토큰 경제와 효율성
        </h3>
        <p style={{ margin: 0, color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          긴 문맥·반복 호출·최고급 모델만 사용하는 Tokenmaxxing은 비용과 지속가능성 문제를 만듭니다. 필요한 맥락만
          제공하고, 작업을 쪼개며, 목적에 맞는 도구를 선택하는 Token-saving 전략이 중요합니다.
        </p>
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>
          TREND 5 — HWPX와 학교 문서
        </h3>
        <p style={{ margin: 0, color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          HWPX는 XML 기반 개방형 문서 포맷으로, 가정통신문·동의서·운영계획 등 학교 문서도 AI와 연결되는 데이터가
          됩니다. 교사에게 AI의 체감은 매일 수정하는 학교 문서가 줄어드는 순간에 더 크게 올 수 있습니다.
        </p>
      </div>

      <h2
        id="순서-3"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        3. 학습지원 소프트웨어 심의
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        학습지원 소프트웨어는 학생 개인정보 처리와 학습 콘텐츠 제공이 수업 운영과 직접 연결됩니다. &ldquo;좋은 앱을
        찾자&rdquo;에서 &ldquo;우리 학교 수업 목표와 학생 보호 기준에 맞는 도구를 선정하자&rdquo;로 전환해야 합니다.
      </p>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '8px', lineHeight: 1.7, fontSize: '14px' }}>
        출처: 교육부 보도자료 「학교의 안전하고 효과적인 학습지원 소프트웨어 선정을 위한 기준 안내」(2025.12.30.)
      </p>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>구분</th>
            <th style={thStyle}>주요 내용</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>
              <strong>필수 기준</strong>
            </td>
            <td style={tdStyle}>
              개인정보 최소 수집·안전 조치, 열람/정정/삭제/처리정지 절차, 만 14세 미만 아동 보호, 보호책임자·제3자
              제공·위탁 정보
            </td>
          </tr>
          <tr>
            <td style={tdStyle}>
              <strong>선택 기준</strong>
            </td>
            <td style={tdStyle}>
              교육 목표 및 학생 특성 적합성, 콘텐츠 품질 및 안전성, 사용 환경 적합성, 접근성 및 사용성, 서비스 운영
              및 지원 체계
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ ...infoBoxStyle, marginTop: '16px', marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: 'var(--Color-text-strong)' }}>학교 단위 선정 절차</h3>
        <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8, color: 'var(--Color-text-normal)', fontSize: '15px' }}>
          <li>
            <strong>의견 수렴</strong> — 교원 의견을 바탕으로 사용 SW 결정
          </li>
          <li>
            <strong>기준 충족 확인</strong> — 필수·선택 기준 충족 여부 판단
          </li>
          <li>
            <strong>안건 상정 및 심의</strong> — 학교운영위원회 심의
          </li>
          <li>
            <strong>확정</strong> — 학교장이 최종 선정
          </li>
        </ol>
        <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
          자주 사용하고 안전성·교육적 효과성이 높은 소프트웨어는 일괄 상정 방식으로 절차를 간소화할 수 있습니다.
        </p>
      </div>

      <h2
        id="순서-4"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        4. SEN 스쿨 활용
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        SEN 스쿨은 학교에서 승인된 학습지원 소프트웨어를 활용하는 플랫폼입니다. Sen 스쿨만이 아니라 다른 에듀테크
        도구도 학교 기준에 맞게 자유롭게 사용할 수 있습니다.
      </p>
      <ul style={{ marginBottom: '16px', lineHeight: 1.8, color: 'var(--Color-text-normal)', fontSize: '15px' }}>
        <li>회원가입 후 학교·교사 계정 연동</li>
        <li>승인된 학습지원 SW 목록 확인 및 수업에 적용</li>
        <li>학생 개인정보 보호 기준에 맞는 도구만 선별 사용</li>
      </ul>
      <div style={{ marginBottom: '32px' }}>{linkButton(SEN_SCHOOL_URL, 'SEN 스쿨(senedu.kr) 바로가기')}</div>

      <h2
        id="순서-5"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        5. Canva · Seam 스페이스
      </h2>

      <div style={infoBoxStyle}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>Canva</h3>
        <p style={{ margin: '0 0 12px 0', color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          디자인 실력이 부족해도 아이디어를 빠르게 시각화할 수 있습니다. 수업 도입 포스터, 카드뉴스, 발표 자료,
          학습 포트폴리오 등에 활용해 보세요.
        </p>
        <ul style={{ margin: '0 0 12px 0', lineHeight: 1.8, color: 'var(--Color-text-normal)', fontSize: '15px' }}>
          <li>아래 초대 링크로 Canva for Education 팀에 가입하세요.</li>
          <li>AI와 결합해 문구·이미지·레이아웃 초안을 빠르게 만들고 수정할 수 있습니다.</li>
        </ul>
        {linkButton(CANVA_INVITE_URL, 'Canva 교육용 팀 초대 링크')}
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>Seam 스페이스</h3>
        <p style={{ margin: '0 0 12px 0', color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          학생들의 마음·생각을 기록하고 분석해 보는 소통 도구입니다. 연수 실습용 계정 정보는 아래와 같습니다.
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>항목</th>
              <th style={thStyle}>내용</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>
                <strong>아이디</strong>
              </td>
              <td style={tdStyle}>teacher1 ~ teacher20</td>
            </tr>
            <tr>
              <td style={tdStyle}>
                <strong>비밀번호</strong>
              </td>
              <td style={tdStyle}>teacher</td>
            </tr>
            <tr>
              <td style={tdStyle}>
                <strong>로그인 주소</strong>
              </td>
              <td style={tdStyle}>
                <a
                  href={SEAMSPACE_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--Color-fill-brand-color)' }}
                >
                  diary.seamspace.me/login
                </a>
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: '12px' }}>{linkButton(SEAMSPACE_LOGIN_URL, 'Seam 스페이스 로그인')}</div>
      </div>

      <h2
        id="순서-6"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        6. 전자칠판 사용 주의사항
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        전자칠판은 큰 모니터가 아니라 교실의 공동 사고 공간이 되어야 합니다. PPT 설명을 넘어 학생 의견 실시간 공유,
        협업 문서 함께 보기, 학생 결과물 비교·피드백에 활용해 보세요.
      </p>

      <table style={{ ...tableStyle, marginBottom: '16px' }}>
        <thead>
          <tr>
            <th style={thStyle}>확인 항목</th>
            <th style={thStyle}>주의사항</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>
              <strong>USB</strong>
            </td>
            <td style={tdStyle}>
              자료 이동·영상 재생·백업 시 지원 파일 형식과 인식 안정성을 확인하세요.
            </td>
          </tr>
          <tr>
            <td style={tdStyle}>
              <strong>32GB</strong>
            </td>
            <td style={tdStyle}>
              파일 저장·앱 설치·캐시 누적 시 용량 관리가 필요합니다. 내장 저장 용량은 모델마다 다를 수 있습니다.
            </td>
          </tr>
          <tr>
            <td style={tdStyle}>
              <strong>모델별 제원</strong>
            </td>
            <td style={tdStyle}>
              같은 제조사라도 모델별 차이가 있으므로 공식 홈페이지·제품 매뉴얼에서 상세 제원을 확인하세요.
            </td>
          </tr>
        </tbody>
      </table>

      <div
        style={{
          ...infoBoxStyle,
          marginBottom: '32px',
          backgroundColor: '#fff8f0',
          borderColor: 'var(--Color-fill-brand-color)',
        }}
      >
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.7 }}>
          <strong>안내:</strong> 전자칠판의 내장 저장 용량은 주로 32GB를 제공하는 경우가 있으나, 모델에 따라 다를 수
          있습니다. USB 활용, 파일 저장, 앱 설치, 영상 자료 저장 등을 계획할 때는 학교에 설치된 모델의 상세 제원을
          반드시 확인해야 합니다. 특히 이솔정보통신 전자칠판을 사용하는 경우, 보유 중인 모델의 내장 저장 용량과 지원
          파일 형식은 이솔정보통신 공식 홈페이지 또는 제품 매뉴얼에서 확인하는 것이 좋습니다.
        </p>
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: 'var(--Color-text-strong)' }}>정리</h3>
        <p style={{ margin: 0, color: 'var(--Color-text-normal)', lineHeight: 1.7, fontSize: '15px' }}>
          스마트한 수업은 도구의 나열이 아니라 흐름의 설계입니다. 디벗(개인 학습 환경) → 학습지원 SW(안전한 도구
          선정) → LMS(자료·과제·피드백) → Canva(창작·표현) → 전자칠판(공동 사고)을 학생의 참여 → 창작 → 피드백 →
          성찰이 이어지도록 하나의 수업 흐름으로 연결해 보세요.
        </p>
      </div>

      <h2
        id="학습지-예시"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        학습지 예시 다운로드
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        갤럭시탭(디벗)에서 활용할 수 있는 간단한 수학 학습지 예시입니다. 수업 설계 참고용으로 다운로드해 보세요.
      </p>
      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        {worksheetDownloadUrl ? (
          <a
            href={worksheetDownloadUrl}
            download="simple_math_worksheet_galaxytab.pdf"
            style={{
              display: 'inline-block',
              marginTop: '8px',
              padding: '10px 18px',
              backgroundColor: 'var(--Color-fill-brand-color)',
              color: 'var(--Color-text-inverse)',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: 'var(--font-family-primary)',
            }}
          >
            {WORKSHEET_DOWNLOAD_LABEL} ↓
          </a>
        ) : (
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
            {worksheetDownloadError ?? '학습지 예시 파일 링크를 불러오는 중입니다…'}
          </p>
        )}
      </div>

      <h2
        id="연수-자료"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        연수 발표 자료 (PPT)
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '8px', lineHeight: 1.7, fontSize: '15px' }}>
        <a
          href={TRAINING_PPT_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--Color-fill-brand-color)', fontWeight: 600 }}
        >
          발표 자료 OneDrive에서 열기
        </a>
        <span style={{ color: 'var(--Color-text-normal)' }}> — 미리보기가 안 보이면 새 탭에서 여세요.</span>
      </p>
      <p style={{ fontSize: '13px', color: 'var(--Color-text-normal)', margin: '0 0 12px 0', lineHeight: 1.5 }}>
        페이지 안 미리보기(OneDrive 공식 임베드)
      </p>
      <div
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid var(--Color-line-normal)',
          width: 'calc(100% + 48px)',
          marginLeft: '-24px',
          marginRight: '-24px',
          aspectRatio: '16 / 9',
          position: 'relative',
          backgroundColor: 'var(--Color-background-normal)',
          marginBottom: '24px',
        }}
      >
        <iframe
          title="PowerPoint Viewer"
          src={TRAINING_PPT_EMBED_SRC}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          loading="lazy"
          allowFullScreen
        />
      </div>
    </div>
  );
};

export default NamdaemoonMiddleSchoolDebethTraining2026;
